import { BillingGridDatasource } from '@/modules/billing-module/components/grid/config/datasources/billing-grid.datasource'
import {
    BillableDeliveryNoteResponse,
    BillableDeliveryNoteStatus
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgColumnDefinition, IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { GridUtils } from '@/shared/util/grid-utils'
import { billingGridDeliveryNotesColumnsDef } from '@/modules/billing-module/components/grid/config/column-defs/billing-grid-delivery-notes.column-defs'
import { BillingCaseSearchGridRequest, BillingPatientSearchGridRequest } from '@/api/services/billing/billing.dto'
import {
    BillingSearcherMode,
    BillingSearcherParams,
    BillingSearcherCaseMode
} from '@/modules/billing-module/components/searcher/billing-searcher.model'
import { StrictNullable } from '@/shared/types/app.types'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { ReportGridRequest } from '@/api/services/common.dto'
import { AxiosResponse } from 'axios'

export class BillingGridDeliveryNotesDatasource extends BillingGridDatasource<BillableDeliveryNoteResponse> {
    private readonly _category: BillableDeliveryNoteStatus

    constructor(laboratoryId: number, mode: BillableDeliveryNoteStatus) {
        super(laboratoryId)
        this._category = mode
    }

    public getColumnDefs(): AgColumnDefinition[] {
        return billingGridDeliveryNotesColumnsDef()
    }
    protected fetchData(filterModel: CommonFilterModel, params: CommonParams): Promise<IResponseAgGrid<BillableDeliveryNoteResponse>> {
        const _filterModel: CommonFilterModel = {
            ...filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingCommonDeliveryNoteService.paginator(_filterModel, params, this.laboratoryId)
    }

    public exporter(request: ReportGridRequest, params: ExportCommonParams): Promise<AxiosResponse<Blob>> {
        const filterModel: CommonFilterModel = {
            ...request.filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingCommonDeliveryNoteService.export(this.laboratoryId, { ...request, filterModel }, params)
    }

    public getSearcherModes(): BillingSearcherMode[] {
        return [
            BillingSearcherMode.PATIENT,
            BillingSearcherMode.CASE,
            BillingSearcherMode.DELIVERY_NOTE,
            BillingSearcherMode.CUSTOMER
        ]
    }
    private async _patientSearcher(patientsIds: number[]): Promise<IResponseAgGrid<BillableDeliveryNoteResponse>> {
        const request: BillingPatientSearchGridRequest = {
            patientsIds
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        return billingCommonDeliveryNoteService.searchPatient(this.laboratoryId, request, params)
    }
    private async _caseSearcher(caseScope: BillingSearcherCaseMode, identifierSearch: string): Promise<StrictNullable<IResponseAgGrid<BillableDeliveryNoteResponse>>> {
        let caseFieldName: BillingCaseSearchGridRequest['caseFieldName'] | undefined
        if (caseScope === BillingSearcherCaseMode.IDENTIFIER) {
            caseFieldName = 'casCasenumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_DOCUMENT) {
            caseFieldName = 'referralNumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_POLICY) {
            caseFieldName = 'insuranceNumber'
        }
        if (caseFieldName && identifierSearch) {
            const request: BillingCaseSearchGridRequest = {
                caseFieldName,
                caseFieldValue: identifierSearch
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingCommonDeliveryNoteService.searchCase(this.laboratoryId, request, params)
        }
        return null
    }
    public async searcher(searcherParams: BillingSearcherParams): Promise<StrictNullable<IResponseAgGrid<BillableDeliveryNoteResponse>>> {
        const { mode, caseScope, patientsIds, identifierSearch } = searcherParams
        if (mode === BillingSearcherMode.PATIENT && patientsIds) {
            return this._patientSearcher(patientsIds)
        } else if (mode === BillingSearcherMode.CASE && caseScope && identifierSearch) {
            return this._caseSearcher(caseScope, identifierSearch)
        } else {
            const filterModel: CommonFilterModel = {}
            if (mode === BillingSearcherMode.CUSTOMER) {
                filterModel['customer.name'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            } else if (mode === BillingSearcherMode.DELIVERY_NOTE) {
                filterModel['label'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingCommonDeliveryNoteService.paginator(filterModel, params, this.laboratoryId)
        }
    }
}
